import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import candidatePortalAPI from '../../services/candidatePortalApi';
import CTCBreakupTable from '../Dashboard/recruitment/CTCBreakupTable';

const STATUS_BANNER = {
  offer_sent: { text: 'You have a new offer', color: 'bg-blue-50 text-blue-800 border-blue-200' },
  accepted: { text: 'Offer Accepted', color: 'bg-green-50 text-green-800 border-green-200' },
  declined: { text: 'Offer Declined', color: 'bg-red-50 text-red-800 border-red-200' }
};

const CandidateOfferView = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [data, setData] = useState(null);
  const [showDeclineForm, setShowDeclineForm] = useState(false);
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const fetchOffer = useCallback(async () => {
    try {
      setLoading(true);
      const res = await candidatePortalAPI.me();
      setData(res.data);
    } catch (err) {
      setError('Could not load your offer. Please log in again.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!candidatePortalAPI.isLoggedIn()) {
      navigate('/candidate-portal/login');
      return;
    }
    fetchOffer();
  }, [fetchOffer, navigate]);

  const handleDecision = async (decision) => {
    try {
      setSubmitting(true);
      await candidatePortalAPI.submitDecision(decision, decision === 'decline' ? reason : undefined);
      setShowDeclineForm(false);
      fetchOffer();
    } catch (err) {
      setError(err.message || 'Failed to submit your decision');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <div className="text-red-600">{error || 'Unable to load offer'}</div>
      </div>
    );
  }

  const banner = STATUS_BANNER[data.stage] || STATUS_BANNER.offer_sent;
  const { offer } = data;
  const breakup = offer?.ctcBreakup;

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4 py-10">
      <div className={`w-full ${breakup ? 'max-w-2xl' : 'max-w-md'} bg-white rounded-xl shadow-sm border border-gray-100 p-8`}>
        <div className={`border rounded-lg px-4 py-2 mb-6 text-sm font-medium ${banner.color}`}>
          {banner.text}
        </div>

        <h1 className="text-lg font-bold text-gray-900 mb-1">Hi {data.name},</h1>
        <p className="text-sm text-gray-500 mb-6">Here are your offer details.</p>

        {breakup ? (
          <div className="mb-6">
            <div className="flex items-center justify-between mb-3">
              <p className="text-xs text-gray-500 uppercase tracking-wide">Annual CTC</p>
              <span className="text-xs font-medium px-2 py-1 rounded bg-blue-50 text-blue-700">{breakup.band?.label}</span>
            </div>
            <p className="text-3xl font-bold text-blue-700 mb-4">
              ₹{(breakup.ctcInput || 0).toLocaleString('en-IN')}
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <CTCBreakupTable side="employee" values={breakup.values} total={breakup.employeeTotal} title="Your Salary (Employee)" />
              <CTCBreakupTable side="employer" values={breakup.values} total={breakup.employerTotal} title="Employer Contributions" />
            </div>
          </div>
        ) : (
          <div className="text-center mb-6">
            <p className="text-xs text-gray-500 uppercase tracking-wide">CTC</p>
            <p className="text-3xl font-bold text-blue-700">
              {offer?.offeredCTC?.amount ? `₹${offer.offeredCTC.amount.toLocaleString()} ${offer.offeredCTC.currency || ''}` : 'N/A'}
            </p>
          </div>
        )}

        <div className="space-y-2 text-sm text-gray-700 mb-6">
          <div className="flex justify-between"><span className="text-gray-500">Designation</span><span>{offer?.designation || 'N/A'}</span></div>
          <div className="flex justify-between"><span className="text-gray-500">Department</span><span>{offer?.department || 'N/A'}</span></div>
          <div className="flex justify-between"><span className="text-gray-500">Work Location</span><span>{offer?.location || 'N/A'}</span></div>
          <div className="flex justify-between">
            <span className="text-gray-500">Date of Joining</span>
            <span>{offer?.joiningDate ? new Date(offer.joiningDate).toLocaleDateString('en-IN') : 'N/A'}</span>
          </div>
        </div>

        {data.stage === 'offer_sent' && !showDeclineForm && (
          <div className="flex gap-3">
            <button
              onClick={() => handleDecision('accept')}
              disabled={submitting}
              className="flex-1 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 disabled:opacity-50"
            >
              Accept Offer
            </button>
            <button
              onClick={() => setShowDeclineForm(true)}
              disabled={submitting}
              className="flex-1 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 disabled:opacity-50"
            >
              Decline Offer
            </button>
          </div>
        )}

        {data.stage === 'offer_sent' && showDeclineForm && (
          <div className="space-y-3">
            <textarea
              placeholder="Reason (optional)"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
            />
            <div className="flex gap-3">
              <button
                onClick={() => handleDecision('decline')}
                disabled={submitting}
                className="flex-1 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 disabled:opacity-50"
              >
                {submitting ? 'Submitting...' : 'Confirm Decline'}
              </button>
              <button
                onClick={() => setShowDeclineForm(false)}
                className="flex-1 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50"
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CandidateOfferView;
